import { IExpiringBatchModel } from "../models/inventoryOverviewModels";
import styles from "../InventoryOverviewPage.module.scss";

interface ExpirationTabProps {
  batches: IExpiringBatchModel[];
  isLoading: boolean;
  canDestroy: boolean;
  onDestroy: (batch: IExpiringBatchModel) => void;
}

const SKELETON_COUNT = 4;

const getRowClass = (days: number): string => {
  if (days <= 3) return styles.row_red;
  if (days <= 10) return styles.row_orange;
  return styles.row_normal;
};

const getDaysBadgeClass = (days: number): string => {
  if (days < 0) return styles.days_expired;
  if (days <= 3) return styles.days_critical;
  if (days <= 10) return styles.days_warning;
  return styles.days_ok;
};

const formatDaysLabel = (days: number): string => {
  if (days < 0) {
    const absDays = Math.abs(days);
    return `Изтекъл преди ${absDays} ${absDays === 1 ? "ден" : "дни"}`;
  }
  if (days === 0) return "Изтича днес";
  if (days === 1) return "Изтича утре";
  return `${days} дни`;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("bg-BG", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const ExpirationSkeleton = () => (
  <div className={styles.skeleton_list}>
    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
      <div key={i} className={styles.skeleton_row}>
        <span className={styles.skeleton_emoji} />
        <span className={styles.skeleton_name} />
        <span className={styles.skeleton_qty} />
        <span className={styles.skeleton_badge} />
      </div>
    ))}
  </div>
);

export const ExpirationTab = ({ batches, isLoading, canDestroy, onDestroy }: ExpirationTabProps) => {
  if (isLoading) {
    return <ExpirationSkeleton />;
  }

  if (batches.length === 0) {
    return (
      <div className={styles.empty_state}>
        <div className={styles.empty_icon}>✨</div>
        <div>Няма продукти с наближаващ срок на годност.</div>
      </div>
    );
  }

  return (
    <div className={styles.list}>
      {batches.map((batch) => {
        const rowClass = getRowClass(batch.daysUntilExpiry);
        const isExpired = batch.daysUntilExpiry < 0;
        const showDestroy = canDestroy && isExpired;

        return (
          <div
            key={`${batch.transferLineId}-${batch.inventoryItemId}`}
            className={`${styles.row} ${rowClass}`}
          >
            <span className={styles.row_emoji}>{batch.categoryEmoji || "📦"}</span>
            <span className={styles.row_name}>{batch.productName}</span>
            <span className={styles.row_qty}>
              {batch.remainingQuantity} бр.
            </span>
            <span className={styles.row_detail}>
              {formatDate(batch.expirationDate)}
            </span>
            <span className={`${styles.row_days_badge} ${getDaysBadgeClass(batch.daysUntilExpiry)}`}>
              {formatDaysLabel(batch.daysUntilExpiry)}
            </span>
            <span className={styles.transfer_number}>
              {batch.transferDisplayNumber}
            </span>
            {showDestroy && (
              <button
                className={styles.destroy_btn}
                onClick={() => onDestroy(batch)}
                type="button"
              >
                Унищожи
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
